import { COMPANY_USER_ROLES, type CompanyUserRole, USER_ROLE_LABELS } from '@crm/shared';
import { Button, Card, CardContent, Checkbox, cn, Input, Label, Select, Spinner } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  CalendarClock,
  Facebook,
  Instagram,
  KeyRound,
  Mail,
  MessageCircle,
  Power,
  ShieldCheck,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ConfirmDialog,
  FormError,
  FormField,
  FormSuccess,
  PageHeader,
  PhotoField,
  XIcon,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useForgotPassword, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { ScheduleEditor } from '@/modules/onboarding';
import { useProfiles } from '@/modules/profiles/hooks';
import { ChangePasswordForm } from '@/modules/settings/components/change-password-form';
import { USERS_ROUTES } from '../constants';
import { useCreateUser, useDeleteUser, useUpdateUser, useUser } from '../hooks';
import {
  type CreateUserFormValues,
  createUserFormSchema,
  type EditUserFormValues,
  editUserFormSchema,
} from '../schemas';

type Aba = 'dados' | 'acesso' | 'horarios' | 'senha';

const ABAS: { key: Aba; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'dados', label: 'Dados pessoais', icon: User },
  { key: 'acesso', label: 'Acesso', icon: ShieldCheck },
  { key: 'horarios', label: 'Horarios', icon: CalendarClock },
  { key: 'senha', label: 'Senha', icon: KeyRound },
];

const GENEROS = [
  { value: '', label: 'Prefiro nao informar' },
  { value: 'FEMALE', label: 'Feminino' },
  { value: 'MALE', label: 'Masculino' },
] as const;

/** O X nao tem icone proprio no lucide - a letra e a marca. */
/** Campo com icone a esquerda - contato e redes. */
function CampoComIcone({
  id,
  label,
  icon,
  placeholder,
  register,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  placeholder?: string;
  register: object;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        <Input id={id} placeholder={placeholder} className="pl-9" {...register} />
      </div>
    </div>
  );
}

/**
 * Formulario de criacao e edicao de usuario. Na edicao ele tem as mesmas tres
 * abas do proprio perfil - dados pessoais, acesso e senha -, porque a conta de
 * quem trabalha no salao e a mesma coisa vista pelo administrador.
 */
/**
 * O papel do SaaS nunca chega neste formulario - ele e de usuario de salao.
 * Se um dia chegar, cai no papel mais baixo em vez de quebrar a tela.
 */
function papelDaEmpresa(role: string): CompanyUserRole {
  return (COMPANY_USER_ROLES as readonly string[]).includes(role)
    ? (role as CompanyUserRole)
    : 'MEMBER';
}

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  useDocumentTitle(isEdit ? 'Editar usuario' : 'Novo usuario');

  const navigate = useNavigate();
  const { user: currentUser } = useSession();
  const profiles = useProfiles({ page: 1, perPage: 100 });
  const existing = useUser(id);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const forgotPassword = useForgotPassword();
  const [pendingRemoval, setPendingRemoval] = useState(false);
  const [aba, setAba] = useState<Aba>('dados');

  const isSelf = isEdit && id === currentUser?.id;

  const form = useForm<CreateUserFormValues>({
    // Na edicao a senha nao e pedida: ela tem aba propria.
    resolver: zodResolver(
      (isEdit ? editUserFormSchema : createUserFormSchema) as typeof createUserFormSchema,
    ),
    values: existing.data
      ? {
          name: existing.data.name,
          email: existing.data.email,
          password: '',
          role: papelDaEmpresa(existing.data.role),
          isActive: existing.data.isActive,
          profileIds: existing.data.profiles.map((profile) => profile.id),
          image: existing.data.image ?? '',
          phone: existing.data.phone ?? '',
          gender: existing.data.gender ?? '',
          instagram: existing.data.instagram ?? '',
          facebook: existing.data.facebook ?? '',
          x: existing.data.x ?? '',
        }
      : undefined,
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'MEMBER',
      isActive: true,
      profileIds: [],
      image: '',
      phone: '',
      gender: '',
      instagram: '',
      facebook: '',
      x: '',
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const mutationError = createUser.error ?? updateUser.error;

  if (isEdit && existing.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (isEdit && existing.isError) {
    return <p className="text-sm text-destructive">{toErrorMessage(existing.error)}</p>;
  }

  function onSubmit(values: CreateUserFormValues | EditUserFormValues) {
    if (isEdit && id) {
      const input: Record<string, unknown> = {
        name: values.name,
        profileIds: values.profileIds,
        image: values.image,
        phone: values.phone,
        gender: values.gender === '' ? null : values.gender,
        instagram: values.instagram,
        facebook: values.facebook,
        x: values.x,
      };

      // Papel e status do proprio usuario sao recusados pela API.
      if (!isSelf) {
        input.role = values.role;
        input.isActive = values.isActive;
      }

      updateUser.mutate({ id, input }, { onSuccess: () => navigate(USERS_ROUTES.LIST) });
      return;
    }

    const created = values as CreateUserFormValues;

    createUser.mutate(
      {
        name: created.name,
        email: created.email,
        password: created.password,
        role: created.role,
        isActive: created.isActive,
        profileIds: created.profileIds,
      },
      { onSuccess: () => navigate(USERS_ROUTES.LIST) },
    );
  }

  const dadosPessoais = (
    <div className="space-y-4">
      <PhotoField
        label="Foto"
        hint="Aparece na agenda e nos atendimentos deste profissional."
        value={watch('image') ?? ''}
        onChange={(value) => setValue('image', value, { shouldDirty: true })}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Nome completo"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />

        {isEdit ? (
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <div className="relative">
              <Mail
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="email"
                value={existing.data?.email ?? ''}
                readOnly
                disabled
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">E o e-mail usado para entrar.</p>
          </div>
        ) : (
          <FormField
            label="E-mail"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
        )}

        <CampoComIcone
          id="phone"
          label="WhatsApp"
          placeholder="(11) 90000-0000"
          icon={<MessageCircle className="h-4 w-4" />}
          register={register('phone')}
        />

        <div className="space-y-2">
          <Label htmlFor="gender">Genero</Label>
          <Select id="gender" {...register('gender')}>
            {GENEROS.map((genero) => (
              <option key={genero.value} value={genero.value}>
                {genero.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-4">
        <p className="text-sm font-medium">Redes sociais</p>

        <div className="grid gap-4 sm:grid-cols-3">
          <CampoComIcone
            id="instagram"
            label="Instagram"
            placeholder="@perfil"
            icon={<Instagram className="h-4 w-4" />}
            register={register('instagram')}
          />
          <CampoComIcone
            id="facebook"
            label="Facebook"
            placeholder="/perfil"
            icon={<Facebook className="h-4 w-4" />}
            register={register('facebook')}
          />
          <CampoComIcone
            id="x"
            label="X"
            placeholder="@perfil"
            icon={<XIcon />}
            register={register('x')}
          />
        </div>
      </div>
    </div>
  );

  const acesso = (
    <div className="space-y-4">
      {!isEdit ? (
        <FormField
          label="Senha inicial"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="role">Papel</Label>
        <Select id="role" disabled={isSelf} {...register('role')}>
          {COMPANY_USER_ROLES.map((role) => (
            <option key={role} value={role}>
              {USER_ROLE_LABELS[role]}
            </option>
          ))}
        </Select>
        {isSelf ? (
          <p className="text-xs text-muted-foreground">
            Voce nao pode alterar o proprio papel ou status.
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Perfis de acesso</Label>
        <p className="text-xs text-muted-foreground">
          Todo o acesso vem dos perfis: quem tem dois perfis fica com o maior nivel de cada modulo.
        </p>
        {profiles.isLoading ? (
          <Spinner className="h-4 w-4 text-muted-foreground" />
        ) : profiles.data && profiles.data.data.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {profiles.data.data.map((profile) => (
              <Checkbox
                key={profile.id}
                className="rounded-md border border-border px-3 py-2"
                label={profile.name}
                value={profile.id}
                {...register('profileIds')}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhum perfil cadastrado ainda.</p>
        )}
      </div>

      <Checkbox
        label="Conta ativa (pode fazer login)"
        disabled={isSelf}
        {...register('isActive')}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Users />}
        title={isEdit ? 'Editar usuario' : 'Novo usuario'}
        description={
          isEdit
            ? 'Atualize os dados, os perfis de acesso e o status da conta.'
            : 'A conta ja nasce apta a fazer login com a senha definida aqui.'
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to={USERS_ROUTES.LIST}>
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Link>
            </Button>

            {/* Ativar e excluir moram aqui, na conta: na lista do celular eram
                mais dois botoes pequenos repetidos linha a linha. Ninguem
                desativa nem apaga a propria conta. */}
            {isEdit && !isSelf ? (
              <>
                <Button
                  variant="outline"
                  disabled={updateUser.isPending || !existing.data}
                  onClick={() =>
                    existing.data &&
                    updateUser.mutate({
                      id: existing.data.id,
                      input: { isActive: !existing.data.isActive },
                    })
                  }
                >
                  <Power className="h-4 w-4" />
                  {existing.data?.isActive ? 'Inativar' : 'Ativar'}
                </Button>

                <Button
                  variant="ghost"
                  disabled={deleteUser.isPending}
                  onClick={() => setPendingRemoval(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {/* Sem abas na criacao: a conta nova nasce de um formulario so, e nao ha
          senha a trocar nem foto de quem ainda nao existe. */}
      {isEdit ? (
        <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
          <nav
            aria-label="Secoes do usuario"
            className="no-scrollbar -mx-3 flex gap-1 overflow-x-auto px-3 sm:mx-0 sm:px-0 lg:flex-col"
          >
            {ABAS.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-current={aba === item.key}
                onClick={() => setAba(item.key)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors lg:w-full',
                  aba === item.key
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                {item.label}
              </button>
            ))}
          </nav>

          <Card variant="flush">
            <CardContent className="space-y-4">
              {aba === 'horarios' ? (
                <ScheduleEditor professionalId={id ?? ''} />
              ) : aba === 'senha' ? (
                isSelf ? (
                  <ChangePasswordForm />
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Senha</p>
                    <p className="text-sm text-muted-foreground">
                      A senha e de quem usa a conta: ninguem, nem o administrador, ve ou define a
                      senha de outra pessoa. Envie o link e {existing.data?.name.split(' ')[0]}{' '}
                      escolhe a nova.
                    </p>

                    {forgotPassword.isSuccess ? (
                      <FormSuccess message={`Link enviado para ${existing.data?.email}.`} />
                    ) : null}

                    <Button
                      variant="outline"
                      disabled={forgotPassword.isPending || !existing.data}
                      onClick={() => existing.data && forgotPassword.mutate(existing.data.email)}
                    >
                      {forgotPassword.isPending ? <Spinner /> : <KeyRound className="h-4 w-4" />}
                      Enviar link de redefinicao
                    </Button>
                  </div>
                )
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <FormError message={mutationError ? toErrorMessage(mutationError) : null} />

                  {aba === 'dados' ? dadosPessoais : acesso}

                  <Button type="submit" disabled={updateUser.isPending}>
                    {updateUser.isPending ? <Spinner /> : null}
                    Salvar alteracoes
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card variant="flush" className="mx-auto max-w-2xl">
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
              <FormError message={mutationError ? toErrorMessage(mutationError) : null} />

              <FormField
                label="Nome completo"
                autoComplete="name"
                error={errors.name?.message}
                {...register('name')}
              />

              <FormField
                label="E-mail"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />

              {acesso}

              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? <Spinner /> : null}
                Criar usuario
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={pendingRemoval}
        title="Excluir usuario"
        message="A conta sai do sistema e perde o acesso. Esta acao nao pode ser desfeita."
        confirmLabel="Excluir"
        loading={deleteUser.isPending}
        onClose={() => setPendingRemoval(false)}
        onConfirm={() => {
          if (id) deleteUser.mutate(id, { onSuccess: () => navigate(USERS_ROUTES.LIST) });
          setPendingRemoval(false);
        }}
      />
    </div>
  );
}
